import { Injectable } from '@angular/core';
import { TranslationService } from 'src/app/core/translation/translation.service';
import { ProjectStreamService } from 'src/app/shared/common/current-project/project-stream.service';
import { DateUtils } from 'src/app/shared/common/utility/date-utils';
import { Feature } from 'src/app/shared/map-data-services/feature/feature';
import { Field, FieldType, LayoutFieldType } from 'src/app/shared/template-services/field';
import { Template } from 'src/app/shared/template-services/template';
import { FeatureField, PageHeaderFeatureField } from './feature-field';
import { SurveyMetadataFieldDefMap, TemplatedFeature, TemplatedFeatureMetadataProperty } from './templated-feature';

/**
 * Interface for device info properties
 */
interface DeviceInfoJSON {
    Product?: string;
    ProductVersion?: string;
    ControllerSerialNumber?: string;
}

/**
 * Service for creating survey templated fields
 * Services are provided at constructor level to avoid repeated parameter passing
 */
@Injectable({
    providedIn: 'root' // Makes it available app-wide as a singleton
})
export class SurveyTemplatedFieldService {
    constructor(private projectStream: ProjectStreamService, private translate: TranslationService) {}

    /**
     * Creates survey templated fields from feature and template
     * Matches the output format of TemplatedFeature.createSurveyTemplatedFields
     *
     * @param feature - Feature containing properties and metadata
     * @param template - Template defining field structure
     * @param fieldCount - Current field ID counter (will be incremented)
     * @param pageTabCount - Current page tab counter (will be incremented)
     * @returns Object containing fields array and updated counters
     */
    createSurveyTemplatedFields(
        feature: Feature,
        template: Template,
        fieldCount: number = 0,
        pageTabCount: number = 0
    ): {
        templatedFeature: TemplatedFeature;
        fields: FeatureField[];
        fieldCount: number;
        pageTabCount: number;
    } {
        if (!feature || !template) {
            return {
                templatedFeature: null,
                fields: [],
                fieldCount,
                pageTabCount
            };
        }

        const featureFields: FeatureField[] = [];
        let currentFieldCount = fieldCount;
        let currentPageTabCount = pageTabCount;

        const templatedFeature = new TemplatedFeature(feature, template);

        // Build groups lookup
        const groups: { [key: string]: { displayName: string; uuid: string } } = {};
        template.groups?.forEach(group => (groups[group.uuid] = group));

        // Process properties section
        if (feature.properties) {
            currentPageTabCount++;

            // Create Properties page header
            featureFields.push({
                type: LayoutFieldType.PageHeader,
                id: currentFieldCount++,
                displayName: this.translate?.instant('TC.Common.Properties') || 'Properties',
                name: 'pageheader',
                pageCount: currentPageTabCount
            } as PageHeaderFeatureField);

            // Process template fields
            let currentGroupId: string = null;

            template.fields.forEach(field => {
                // Check if field should be included
                if (!this.shouldIncludeField(field, feature)) {
                    return;
                }

                // Use `createFeatureField()` from `TemplatedFeature` instance
                const featureField = templatedFeature.createFeatureField(feature, field.toDTO());

                // Handle group headers
                if (field.groupId && currentGroupId !== field.groupId) {
                    currentGroupId = field.groupId;
                    featureFields.push({
                        type: LayoutFieldType.Group,
                        id: Number(field.groupId),
                        displayName: groups[field.groupId]?.displayName || 'Group'
                    } as FeatureField);
                }

                featureFields.push(featureField);
            });
        }

        // Process metadata section
        if (feature.metadata) {
            const metadata = this.createMetadataFields(feature, currentFieldCount, currentPageTabCount);

            // Update counters from metadata processing
            currentFieldCount = metadata.nextFieldId;
            currentPageTabCount = metadata.nextPageTabCount;

            // Add metadata fields
            featureFields.push(...metadata.fields);
        }

        return {
            templatedFeature,
            fields: featureFields,
            fieldCount: currentFieldCount,
            pageTabCount: currentPageTabCount
        };
    }

    /**
     * Creates metadata fields for a feature based on available metadata
     *
     * @param feature - The feature containing metadata
     * @param startingFieldId - Starting ID for field numbering
     * @param startingPageTab - Starting page tab count
     * @returns Object containing the generated fields and updated counters
     */
    createMetadataFields(
        feature: Feature,
        startingFieldId: number = 0,
        startingPageTab: number = 0
    ): { fields: FeatureField[]; nextFieldId: number; nextPageTabCount: number } {
        if (!feature || !feature.metadata) {
            return {
                fields: [],
                nextFieldId: startingFieldId,
                nextPageTabCount: startingPageTab
            };
        }

        const featureFields: FeatureField[] = [];
        let fieldId = startingFieldId;
        let pageTabCount = startingPageTab;

        // Increment page tab count
        pageTabCount++;

        // Create tab menu page for 'Metadata'
        featureFields.push({
            type: LayoutFieldType.PageHeader,
            id: fieldId++,
            displayName: this.translate?.instant('TC.Common.Metadata'),
            name: 'pageheader1',
            pageCount: pageTabCount
        } as PageHeaderFeatureField);

        // feature.metadata list to fields
        Object.keys(feature.metadata)
            .filter(key => !!SurveyMetadataFieldDefMap[key as TemplatedFeatureMetadataProperty])
            .sort(
                (a, b) =>
                    Object.keys(SurveyMetadataFieldDefMap).indexOf(a) -
                    Object.keys(SurveyMetadataFieldDefMap).indexOf(b)
            )
            .forEach(key => {
                const metadataField = SurveyMetadataFieldDefMap[key as TemplatedFeatureMetadataProperty];
                if (metadataField) {
                    let field: FeatureField = {
                        id: fieldId++,
                        uuid: key, // using key as uuid for metadata fields
                        name: key,
                        displayName: this.translate.instant(metadataField.displayName),
                        type: metadataField.fieldType || FieldType.Text,
                        groupId: null,
                        required: false,
                        value: feature.metadata[key],
                        hasError: false
                    };

                    // clean up values for specific fields
                    switch (key) {
                        case TemplatedFeatureMetadataProperty.COLLECTION_COLLECTED_BY:
                            this.projectStream
                                ?.getCurrentProjectUsers()
                                .then(
                                    (users: Array<{ id: string; name: string }>) =>
                                        (field.value =
                                            users?.find((u: { id: string; name: string }) => u.id === field.value)
                                                ?.name ||
                                            this.translate.instant('TC.Common.Unknown') ||
                                            '')
                                );
                            break;
                        case TemplatedFeatureMetadataProperty.COLLECTION_DEVICE_INFO:
                            field.value = this.parseDeviceInfo(field.value);
                            break;
                        case TemplatedFeatureMetadataProperty.SURVEY_XPOSITION:
                        case TemplatedFeatureMetadataProperty.SURVEY_YPOSITION:
                        case TemplatedFeatureMetadataProperty.SURVEY_ZPOSITION:
                            field.value = Number(field.value).toFixed(3);
                            break;
                        case TemplatedFeatureMetadataProperty.COLLECTION_UTC:
                            field.value = DateUtils.formatDateFromDateTime(field.value, 'mediumdatetime12hr', false);
                            break;
                    }
                    featureFields.push(field);
                }
            });

        return {
            fields: featureFields,
            nextFieldId: fieldId,
            nextPageTabCount: pageTabCount
        };
    }

    /**
     * Parse device info string into a formatted display string
     *
     * @param deviceInfo - The device info JSON string
     * @returns Formatted device info string
     */
    private parseDeviceInfo(deviceInfo: string): string {
        // Handle empty input
        if (!deviceInfo) return '';

        try {
            // Parse device info
            const deviceInfoJSON = JSON.parse(deviceInfo) as DeviceInfoJSON;

            // Format and return device info string
            return `${deviceInfoJSON?.Product || ''} - ${deviceInfoJSON.ProductVersion || ''} - ${
                deviceInfoJSON?.ControllerSerialNumber || 'N/A'
            }`;
        } catch {
            // Gracefully handle invalid JSON
            return '';
        }
    }

    /**
     * Determines if a field should be included in the survey feature
     *
     * @param field - Template field to check
     * @param feature - Feature containing properties
     * @returns true if field should be included
     */
    private shouldIncludeField(field: Field, feature: Feature): boolean {
        if (!field || !feature) return false;

        // Check if property exists
        const hasFieldAndFeatureProperty = field.name && !!feature.properties?.hasOwnProperty(field.name.toLowerCase());

        // Check if property or field values exist
        const hasPropertyAndValue = !!feature?.properties || !!field.values?.length;

        // TODO: remove `hasImages` after TCMAPS-4618 is finalized OR if starting to fix TCMAPS-4997
        // Skip image and signature fields for now
        const hasImages = field.type === FieldType.Image || field.type === FieldType.Signature;

        if (!hasFieldAndFeatureProperty || !hasPropertyAndValue || hasImages) {
            // Skip the field if any of the conditions are met
            return false;
        }

        return true;
    }
}
